import { ReactNode } from 'react'

import { getLocation } from 'hooks/useHistory'
import { Toast, showToast } from '.'
import { MessageVariant } from './Root'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { Button } from 'blaze-ui/Button'
import { Spinner } from '../Spinner/Spinner'
import { responsiveDesignLogic } from 'hooks/useResponsiveDesign'

import { ReactComponent as RightArrow } from 'assets/icons/arrowRight16.svg'
import { ReactComponent as CloseIcon } from 'assets/icons/close16.svg'
import { ReactComponent as ReviewIcon } from 'assets/icons/reviews32.svg'
import { ReactComponent as DocumentIcon } from 'assets/icons/review/reviewPencil.svg'
import { ReactComponent as HandbookIcon } from 'assets/icons/handbookSolid.svg'
import { CheckBroken, InformationCircleContained } from 'blaze-ui/icons'
import AlertTriangle from 'blaze-ui/icons/20/AlertTriangle'
import { Star } from 'blaze-ui/icons'

type MessageButtonVariants = 'proceed' | 'cancel'

export interface MessageButtonProps {
  variant: MessageButtonVariants
  label?: string
  onPress?: () => void
  type: 'link' | 'button'
  to?: string
}

interface ToastMessageProps {
  toastMessage: string | ReactNode
  variant?: MessageVariant
  buttonProps?: MessageButtonProps
  icon?: ReactNode
  theme?: 'dark' | 'light'
}

export const ToastMessage = ({
  toastMessage,
  variant = 'success',
  buttonProps,
  icon,
  theme = 'light',
}: ToastMessageProps) => {
  const variantIcons = {
    success: <CheckBroken size={24} />,
    error: <AlertTriangle size={24} />,
    loading: <Spinner />,
    review: <ReviewIcon />,
    handbook: <HandbookIcon />,
    document: <DocumentIcon />,
    generating: <Spinner />,
    info: <InformationCircleContained size={24} />,
    pink: <Star />,
    rating: undefined,
    designRating: undefined,
    generatingSocialCta: undefined,
    singleLine: undefined,
  }

  const toastIcon = icon || variantIcons[variant]

  // eslint-disable-next-line no-nested-ternary
  const buttonEndIcon = buttonProps ? (buttonProps.variant === 'cancel' ? CloseIcon : RightArrow) : undefined

  return (
    <Toast.Root variant={variant} theme={theme}>
      <Toast.Details>
        {toastIcon && <Toast.Icon variant={variant}>{toastIcon}</Toast.Icon>}
        <Toast.Description wide={variant === 'singleLine'}>{toastMessage}</Toast.Description>
      </Toast.Details>
      <Toast.Action>
        {!!buttonProps && buttonProps.type === 'link' && buttonProps.to && (
          <ButtonLink
            variant="tertiary"
            size="sm"
            endIcon={buttonProps.label ? undefined : buttonEndIcon}
            to={buttonProps.to}
          >
            {buttonProps.label}
          </ButtonLink>
        )}
        {!!buttonProps && buttonProps.type === 'button' && (
          <Button
            variant="tertiary"
            size="sm"
            endIcon={buttonProps.label ? undefined : buttonEndIcon}
            onPress={buttonProps.onPress}
          >
            {buttonProps.label}
          </Button>
        )}
      </Toast.Action>
    </Toast.Root>
  )
}

export const showToastMessage = (
  toastMessage: string | ReactNode,
  variant: MessageVariant = 'success',
  buttonProps?: MessageButtonProps,
  duration: number | undefined = 5,
  icon?: ReactNode,
  theme?: 'dark' | 'light',
) => {
  if (/disable_toasters/.test(getLocation().search)) return showToast(null, 0)

  return showToast(
    <ToastMessage toastMessage={toastMessage} variant={variant} buttonProps={buttonProps} icon={icon} theme={theme} />,
    duration,
  )
}

export const showIntegrationSuccessToast = (message: string, icon?: ReactNode, theme?: 'dark' | 'light') => {
  const { isMobile } = responsiveDesignLogic()

  const dismiss = showToast(
    <div onClick={() => isMobile && dismiss()}>
      <ToastMessage toastMessage={message} variant="success" icon={icon} theme={theme} />
    </div>,
    0,
  )

  setTimeout(() => dismiss(), isMobile ? 3000 : 5000)
  return dismiss
}
